/**
 * Statement export: the deal ledger as CSV, with a running balance.
 *
 * ## The running balance is a fold of the server's own deltas
 *
 * Each deal row carries `balance_delta_minor`, the exact change it made to the
 * balance (docs/10 §4). A statement is, by definition, the prefix sums of those
 * deltas — so the column is computed as `opening + Σ delta`, in `BigInt`, from
 * the account's current balance backwards. No formula, no rounding, no currency
 * conversion.
 *
 * The opening balance is derived once: `current_balance − Σ all deltas`, using
 * **every** deal the account has (not just the filtered range), so filtering the
 * table to a period does not restart the running balance at an arbitrary number.
 *
 * ## Why CSV on the client
 *
 * The export is a view of data the trader can already see; generating it in the
 * browser keeps it out of the money path (nothing is recalculated) and needs no
 * service change. P8 owns the *official* statement, generated as a pure fold of
 * the journal on the server; this file says so in its header.
 */

import type { DealRecord } from '../data/types.js';

export interface RunningDeal extends DealRecord {
  /** Account balance after this deal, in minor units (canonical string). */
  readonly balance_after_minor: string;
}

/**
 * Attach a running balance to an ascending list of deals.
 *
 * `currentBalanceMinor` is the server's balance *now*; `deals` must be every
 * deal since the account opened, in `seq` order, for the anchor to be exact.
 */
export function withRunningBalance(
  deals: readonly DealRecord[],
  currentBalanceMinor: string,
): RunningDeal[] {
  let total = 0n;
  for (const deal of deals) total += BigInt(deal.balance_delta_minor);
  let running = BigInt(currentBalanceMinor) - total;
  const out: RunningDeal[] = [];
  for (const deal of deals) {
    running += BigInt(deal.balance_delta_minor);
    out.push({ ...deal, balance_after_minor: running.toString() });
  }
  return out;
}

function escapeCell(value: string): string {
  if (value.includes('"') || value.includes(',') || value.includes('\n') || value.includes('\r')) {
    return `"${value.replaceAll('"', '""')}"`;
  }
  return value;
}

/**
 * Render a statement as CSV (RFC 4180 quoting).
 *
 * `digits` is the account currency's minor-unit digits, used only to print the
 * decimal point; `currency` labels the money columns. Numbers stay canonical
 * strings in the machine-readable columns so a spreadsheet reads them exactly.
 */
export function statementCsv(
  deals: readonly RunningDeal[],
  options: { currency: string; digits: number; accountId: string; generatedAt: string },
): string {
  const lines: string[] = [];
  lines.push(
    '# Weke Forex — deal statement (terminal export; the official statement is generated by the platform from the journal)',
  );
  lines.push(
    `# account,${escapeCell(options.accountId)},currency,${escapeCell(options.currency)},generated_at,${escapeCell(options.generatedAt)}`,
  );
  lines.push(
    [
      'seq',
      'time',
      'deal_id',
      'symbol',
      'type',
      'side',
      'volume_lots_e4',
      'price_e8',
      'realised_pnl_minor',
      'swap_minor',
      'commission_minor',
      'balance_delta_minor',
      'balance_after_minor',
      'order_id',
      'position_id',
    ].join(','),
  );
  for (const deal of deals) {
    lines.push(
      [
        deal.seq,
        deal.created_at,
        deal.deal_id,
        deal.symbol,
        deal.deal_type,
        deal.side,
        deal.volume_e4,
        deal.price_e8,
        deal.realised_pnl_minor,
        deal.swap_minor,
        deal.commission_minor,
        deal.balance_delta_minor,
        deal.balance_after_minor,
        deal.order_id ?? '',
        deal.position_id ?? '',
      ]
        .map(escapeCell)
        .join(','),
    );
  }
  return `${lines.join('\r\n')}\r\n`;
}

/** Sum of realised P&L, swap and commission over a set of deals (exact). */
export function totals(deals: readonly DealRecord[]): {
  realisedMinor: string;
  swapMinor: string;
  commissionMinor: string;
  balanceDeltaMinor: string;
} {
  let realised = 0n;
  let swap = 0n;
  let commission = 0n;
  let balance = 0n;
  for (const deal of deals) {
    realised += BigInt(deal.realised_pnl_minor);
    swap += BigInt(deal.swap_minor);
    commission += BigInt(deal.commission_minor);
    balance += BigInt(deal.balance_delta_minor);
  }
  return {
    realisedMinor: realised.toString(),
    swapMinor: swap.toString(),
    commissionMinor: commission.toString(),
    balanceDeltaMinor: balance.toString(),
  };
}
